import { useAtomValue } from 'jotai';

import {
  offlineDismissedSyncedBadgeIdsAtom,
  offlineSyncQueueAtom,
} from '@/atoms/offline-recording';
import { useIsOfflineRecordingEnabled } from '@/features/flags';
import { findOfflineSyncQueueItem } from '@/hooks/desktop/offline-sync-queue-lookup';
import { isTauri } from '@/tauri/utils';
import { useCurrentSessionId } from '@heidi/runtime/shell';

import { useOfflineAwareSyncStatus } from './hooks/useOfflineAwareSyncStatus';
import { SyncStatusBadge } from './sync-status-badge';

interface SessionSyncStatusBadgeProps {
  className?: string;
  'data-testid'?: string;
}

/**
 * Resolves the current session's offline sync status from the sync queue and
 * renders the shared `SyncStatusBadge`. Surface-agnostic on purpose: both the
 * V1 session header (`HeaderControls`) and the V2 header (`v2/header`) render
 * this same component next to the Transcribe / Create controls, so the badge
 * can never drift between the two detail-page layouts.
 *
 * Why each guard exists:
 * - `isTauri()`: defense-in-depth so the badge can never leak onto web, even if
 *   the queue is somehow non-empty there (mirrors the sibling offline surfaces).
 * - Flag gate: renders nothing when the offline-recording rollout is off.
 * - Hidden while capturing (`recording`): that's the recorder's affordance, not
 *   this badge; also hidden when the session has no sync entry.
 * - Matches by local or server session id, skipping an abandoned `recording`
 *   shell that only carries the server id: `findOfflineSyncQueueItem` is shared
 *   with `useOfflineNoteOverride` so the badge and the note body resolve the
 *   same session to the same row.
 * - Offline-aware: an in-flight status is read as `pending_sync` while the app
 *   is offline, so the spinner cannot run alongside the offline banner
 *   (`useOfflineAwareSyncStatus`).
 * - Dismissed synced ids: the tick is an acknowledgement, so it stops once it
 *   has had its moment. Subtracted here rather than by clearing the queue row —
 *   see `scheduleSyncedDismissal` in `useOfflineRecordingEvents` for why the row
 *   has to stay.
 */
export const SessionSyncStatusBadge = ({
  className,
  'data-testid': dataTestId = 'session-sync-status-badge',
}: SessionSyncStatusBadgeProps) => {
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  const sessionId = useCurrentSessionId();
  const queue = useAtomValue(offlineSyncQueueAtom);
  const dismissedSyncedIds = useAtomValue(offlineDismissedSyncedBadgeIdsAtom);

  const item = findOfflineSyncQueueItem(queue, sessionId);
  // Before the early returns, so hook order stays unconditional. An in-flight
  // status while the app is offline reads as `pending_sync` — see the hook.
  const status = useOfflineAwareSyncStatus(item?.status);

  if (!isTauri() || !isOfflineRecordingEnabled || !sessionId) {
    return null;
  }

  if (!status || status === 'recording') {
    return null;
  }

  // Dismissed-tick guard — rationale in this component's JSDoc above.
  if (status === 'synced' && dismissedSyncedIds.includes(sessionId)) {
    return null;
  }

  return (
    <SyncStatusBadge status={status} className={className} data-testid={dataTestId} />
  );
};

SessionSyncStatusBadge.displayName = 'SessionSyncStatusBadge';
